import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Square, Upload, Activity, Zap } from 'lucide-react';
import { NativeAudio } from '../native/nativeAudio/index';

const AdminNativeAudioLab = () => {
  const navigate = useNavigate();

  const [status, setStatus] = useState('Idle');
  const [lastResult, setLastResult] = useState(null);

  const [eqFrequency, setEqFrequency] = useState(6000);
  const [eqGain, setEqGain] = useState(3);
  const [eqBandwidth, setEqBandwidth] = useState(1);

  const [audiogram, setAudiogram] = useState({
    125: 5,
    250: 5,
    500: 10,
    1000: 15,
    2000: 25,
    4000: 40,
    8000: 50,
  });

  const [tinnitusFreq, setTinnitusFreq] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const run = async (label, fn) => {
    try {
      setStatus(`${label}...`);
      const res = await fn();
      console.log(`[NativeAudioLab] ${label}`, res);
      setLastResult(res);
      setStatus(`${label} OK`);
    } catch (err) {
      console.error(`[NativeAudioLab] ${label} ERROR`, err);
      setLastResult({ error: err?.message || String(err) });
      setStatus(`${label} ERROR`);
    }
  };

  const ping = () => run('PING', () => NativeAudio.ping());

  const load = () =>
    run('LOAD', () =>
      NativeAudio.loadBundled({
        filename: 'test',
        ext: 'mp3',
      })
    );

  const play = () => run('PLAY', () => NativeAudio.play());
  const pause = () => run('PAUSE', () => NativeAudio.pause());

  const stop = () =>
    run('STOP', async () => {
      if (NativeAudio.stop) return NativeAudio.stop();
      return NativeAudio.pause();
    });

  const state = () =>
    run('STATE', async () => {
      if (NativeAudio.getState) return NativeAudio.getState();
      return { available: false, reason: 'getState not implemented' };
    });

  const eqOff = () =>
    run('EQ OFF', () =>
      NativeAudio.setEQ({
        enabled: false,
      })
    );

  const eqSoft = () =>
    run('EQ SOFT', () =>
      NativeAudio.setEQ({
        enabled: true,
        frequency: 6000,
        gain: 3,
        bandwidth: 1,
        type: 'parametric',
      })
    );

  const eqStrong = () =>
    run('EQ STRONG', () =>
      NativeAudio.setEQ({
        enabled: true,
        frequency: 1000,
        gain: -24,
        bandwidth: 4,
        type: 'parametric',
      })
    );

  const applyCustomEQ = () =>
    run('EQ CUSTOM', () =>
      NativeAudio.setEQ({
        enabled: true,
        frequency: Number(eqFrequency),
        gain: Number(eqGain),
        bandwidth: Number(eqBandwidth),
        type: 'parametric',
      })
    );
  const HEAREN_EQ_BANDS = [250, 500, 1000, 2000, 4000, 6000, 8000];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const computeHearenGain = (lossOrGain, frequency) => {
  const input = Math.max(0, Number(lossOrGain) || 0);

  let gain = input * 0.28;

  if (frequency >= 1000 && frequency <= 2000) {
    gain *= 1.05;
  }

  if (frequency >= 4000) {
    gain *= 0.45;
  }

  const maxGain = frequency >= 4000 ? 2.5 : 4.0;

  return clamp(gain, 0, maxGain);
};

const buildHearenBands = (sourceAudiogram) =>
  HEAREN_EQ_BANDS.map((frequency) => ({
    frequency,
    gain: Number(computeHearenGain(sourceAudiogram[frequency], frequency).toFixed(1)),
    bandwidth: 1.0,
  }));

const multiEqOn = () =>
  run('MULTI EQ ON', () =>
    NativeAudio.setMultiBandEQ({
      enabled: true,
      bands: buildHearenBands(audiogram),
    })
  );

const multiEqOff = () =>
  run('MULTI EQ OFF', () =>
    NativeAudio.setMultiBandEQ({
      enabled: false,
      bands: [],
    })
  );
  const loadHearenProfile = async () => {
  try {
    setStatus('LOAD PROFILE...');
    console.log('[Lab] Loading Hearen profile...');

    const token =
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('auth_token');

    const API_BASE_URL =
      process.env.REACT_APP_BACKEND_URL || 'https://hearen-backend.onrender.com';

    let labProgramData = null;

    const labResponse = await fetch(`${API_BASE_URL}/api/lab/programs`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (labResponse.ok) {
      const labData = await labResponse.json();

      if (labData.active_program && labData[labData.active_program]) {
        const activeProgram = labData[labData.active_program];

        labProgramData = {
          program_type: labData.active_program,
          f_t: activeProgram.f_t,
          gain_db: activeProgram.gain_db,
          enabled: true,
        };
      }
    }

    const userId =
      localStorage.getItem('user_id') ||
      localStorage.getItem('userId') ||
      sessionStorage.getItem('user_id') ||
      sessionStorage.getItem('userId');

    const response = await fetch(`${API_BASE_URL}/api/equalizer/${userId}`);

    if (!response.ok) {
      throw new Error(`Equalizer fetch failed: ${response.status}`);
    }

    const equalizerData = await response.json();

    if (labProgramData?.enabled) {
      equalizerData.has_inverse_notch = true;
      equalizerData.inverse_notch = {
        center_frequency: labProgramData.f_t,
        bandwidth: 200,
        gain: labProgramData.gain_db,
      };
      equalizerData.lab_mode_active = true;
      equalizerData.lab_program_type = labProgramData.program_type;
    }

    console.log('[Lab] Profile received', equalizerData);

    const gains = equalizerData?.audiometric_gains || [];
    const mapped = {};

    gains.forEach((g) => {
      mapped[g.frequency] = g.gain ?? 0;
    });

    if (Object.keys(mapped).length > 0) {
      setAudiogram(mapped);
    } else {
      console.warn('[Lab] No audiometric gains received — keeping current audiogram');
    }
    setTinnitusFreq(equalizerData?.inverse_notch?.center_frequency || null);
    setProfileLoaded(true);
    setLastResult(equalizerData);
    setStatus('LOAD PROFILE OK');
  } catch (err) {
    console.error('[Lab] Profile load error', err);
    setLastResult({ error: err?.message || String(err) });
    setStatus('LOAD PROFILE ERROR');
  }
};

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white pb-32">
      <div className="sticky top-0 z-30 px-4 py-3 flex items-center gap-3 bg-[#0a0a1a]/95 border-b border-white/10">
        <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-white/10">
          <ArrowLeft size={18} className="text-white/70" />
        </button>

        <div>
          <h1 className="text-white font-semibold text-base">Native Audio Lab</h1>
          <p className="text-white/40 text-xs">Sandbox iOS — moteur audio natif</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 py-6 space-y-6">
        <section className="rounded-2xl p-5 bg-white/[0.04] border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={16} className="text-amber-400" />
            <h2 className="text-sm font-medium text-white/80">
              Contrôles natifs — MULTI EQ BUILD
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button onClick={ping} className="lab-btn">PING</button>
            <button onClick={load} className="lab-btn"><Upload size={15} /> LOAD</button>
            <button onClick={play} className="lab-btn"><Play size={15} /> PLAY</button>
            <button onClick={pause} className="lab-btn"><Pause size={15} /> PAUSE</button>
            <button onClick={stop} className="lab-btn"><Square size={15} /> STOP</button>
            <button onClick={state} className="lab-btn"><Activity size={15} /> STATE</button>
            <button onClick={eqOff} className="lab-btn">EQ OFF</button>
            <button onClick={eqSoft} className="lab-btn">EQ SOFT</button>
            <button onClick={eqStrong} className="lab-btn">EQ STRONG</button>

            <button onClick={multiEqOn} className="lab-btn">MULTI EQ ON</button>
            <button onClick={multiEqOff} className="lab-btn">MULTI EQ OFF</button>
          </div>
        </section>

        <section className="rounded-2xl p-5 bg-white/[0.04] border border-white/10 space-y-5">
          <h2 className="text-sm font-medium text-white/80">EQ Custom</h2>

          <div>
            <div className="flex justify-between text-xs text-white/50 mb-2">
              <span>Fréquence</span>
              <span>{eqFrequency} Hz</span>
            </div>
            <input
              type="range"
              min="125"
              max="12000"
              step="25"
              value={eqFrequency}
              onChange={(e) => {
                const value = Number(e.target.value);
                setEqFrequency(value);
                NativeAudio.setEQ({
                  enabled: true,
                  frequency: value,
                  gain: eqGain,
                  bandwidth: eqBandwidth,
                  type: 'parametric',
                });
              }}
              className="w-full"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs text-white/50 mb-2">
              <span>Gain</span>
              <span>{eqGain} dB</span>
            </div>
            <input
              type="range"
              min="-24"
              max="24"
              step="0.5"
              value={eqGain}
              onChange={(e) => {
                const value = Number(e.target.value);
                setEqGain(value);
                NativeAudio.setEQ({
                  enabled: true,
                  frequency: eqFrequency,
                  gain: value,
                  bandwidth: eqBandwidth,
                  type: 'parametric',
                });
              }}
              className="w-full"
            />
          </div>

          <div>
            <div className="flex justify-between text-xs text-white/50 mb-2">
              <span>Bandwidth</span>
              <span>{eqBandwidth}</span>
            </div>
            <input
              type="range"
              min="0.2"
              max="6"
              step="0.1"
              value={eqBandwidth}
              onChange={(e) => {
                const value = Number(e.target.value);
                setEqBandwidth(value);
                NativeAudio.setEQ({
                  enabled: true,
                  frequency: eqFrequency,
                  gain: eqGain,
                  bandwidth: value,
                  type: 'parametric',
                });
              }}
              className="w-full"
            />
          </div>

          <button onClick={applyCustomEQ} className="lab-btn w-full">
            APPLY EQ CUSTOM
          </button>
        </section>

        <section className="rounded-2xl p-5 bg-white/[0.04] border border-white/10 space-y-4">
          <h2 className="text-sm font-medium text-white/80">Profil auditif</h2>

          <button onClick={loadHearenProfile} className="lab-btn w-full">
            LOAD HEAREN PROFILE
          </button>

          <p className="text-white/30 text-xs">
            {profileLoaded ? 'Profil chargé depuis Hearen' : 'Valeurs de test affichées par défaut'}
          </p>

          <div className="space-y-2">

  {Object.keys(audiogram).length === 0 && (
    <p className="text-xs text-white/40">
      Aucune correction audiométrique active
    </p>
  )}

  {Object.keys(
    Object.keys(audiogram).length > 0
      ? audiogram
      : {
          125: null,
          250: null,
          500: null,
          1000: null,
          2000: null,
          4000: null,
          8000: null,
        }
  ).map((freq) => {
    const loss = audiogram[freq];

    return (
      <div
        key={freq}
        className="flex justify-between items-center text-xs text-white/60 py-1 border-b border-white/5 last:border-b-0"
      >
        <span>{freq} Hz</span>
        <span>
          {loss !== undefined && loss !== null
            ? `${loss} dB HL`
            : '—'}
        </span>
      </div>
    );
  })}
</div>

          <div className="pt-2 border-t border-white/10">
            <div className="flex justify-between text-xs text-white/60">
              <span>Acouphène</span>
              <span>{tinnitusFreq ? `${tinnitusFreq} Hz` : '—'}</span>
            </div>
          </div>
        </section>

        <section className="rounded-2xl p-5 bg-white/[0.04] border border-white/10">
          <p className="text-white/40 text-xs uppercase mb-2">Status</p>
          <p className="text-white/80 text-sm font-mono">{status}</p>
        </section>

        <section className="rounded-2xl p-5 bg-white/[0.04] border border-white/10">
          <p className="text-white/40 text-xs uppercase mb-2">Dernier résultat</p>
          <pre className="text-white/60 text-xs overflow-auto whitespace-pre-wrap">
            {lastResult ? JSON.stringify(lastResult, null, 2) : '—'}
          </pre>
        </section>
      </div>
    </div>
  );
};

export default AdminNativeAudioLab;